<template>
  <div
    ref="dropEl"
    class="scrollable-list flex flex-auto flex-col overflow-hidden bg-bg-base-bg2 h-full w-full pb-6 tablet:pb-1"
    :class="panelClass"
  >
    <div class="relative z-30 flex gap-x-4 border-b border-b-bor-nav bg-bg-base-bg1 p-4">
      <wui-btn
        v-if="showBackButton"
        data-test="back-button"
        icon
        prepend-icon="arrowLeft"
        variant="neut"
        state="alpha"
        @click.stop="emit('close')"
      />
      <div>
        <p class="mb-2 text-1416">
          {{ $t('common.discussion') }}
        </p>
        <p class="text-1316 text-fg-base-fg22">
          {{ commentsText }}
        </p>
      </div>
    </div>
    <div class="relative flex min-h-0 min-w-0 grow flex-col">
      <user-feature component-name="ChatFilesDragAndDrop">
        <file-drag-and-drop v-if="overDropEl" />
      </user-feature>
      <chat-event-list
        v-if="events.length"
        class="mt-2 grow"
        :events="events"
        :room="room"
        :hide-quotes="true"
        :disable-scroll-loading="true"
        :disable-history-loading="true"
        :prevent-lazy-load="true"
      />
      <div
        v-else
        class="flex grow flex-col items-center justify-center"
      >
        <wui-icon
          name="messageM"
          class="mb-4 h-20! w-20! text-primary-tints-250!"
        />
        <p
          class="text-primary text-1316 max-w-[214px] text-center"
          data-test="text-for-empty-events-with-image"
        >
          {{ $t('chat.youCanLeaveCommentOnMessage') }}
        </p>
      </div>
      <user-feature component-name="ChatFooter">
        <chat-footer
          class="m-2"
          :room="room"
          :event-id-for-quoting="eventIdForQuoting"
        />
      </user-feature>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiBtn, WuiIcon } from '@wui/common-library'
import { HistoryMessageItem } from '@wui/im'
import { computed, ComputedRef, defineAsyncComponent, nextTick, onMounted, onUnmounted, watch } from 'vue'

import { ChatEventList } from '@/widgets/chat-event-list'
import { ChatFooter } from '@/widgets/chat-footer'


import { useFilesDropHandler } from '@/features/drop-files'

import { UserFeature } from '@/shared/components/user-feature'
import { useDragAndDrop } from '@/shared/composables/use-drag-and-drop'
import { Room } from '@/shared/types/store'

const FileDragAndDrop = defineAsyncComponent(() => import('@/features/file-drag-and-drop/ui/file-drag-and-drop.vue'))

const props = defineProps<{
  room: Room
  events: HistoryMessageItem[]
  eventIdForQuoting: number
  commentsText: string
  showBackButton?: boolean
  panelClass?: string
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const roomRef = computed(() => props.room)
const { dropHandler } = useFilesDropHandler(roomRef as ComputedRef<Room>)
const { dropEl, overDropEl, init, destroy } = useDragAndDrop(dropHandler)

const initDragAndDrop = async () => {
  await nextTick()
  if (dropEl.value) {
    init()
  }
}

onMounted(() => {
  void initDragAndDrop()
})

watch(() => props.room, () => {
  destroy()
  void initDragAndDrop()
})

onUnmounted(() => {
  destroy()
})
</script>
